import React, { useState, useEffect, useRef } from "react";
import { Mail, X } from "lucide-react";
import { usePostAuth } from "../../hooks/usePostAuth";
import { useQueryClient } from "@tanstack/react-query";
import { INVITE_URL } from "../../utils/helpers";
import { motion, AnimatePresence } from "motion/react";
import {
  messageVariants,
  modalBackdropVariants,
  modalContentVariants,
} from "../../animations/animation";

interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InvitationPayload {
  email: string;
  role: "user";
  accept_url: string;
}

export default function InvitationModal({
  isOpen,
  onClose,
}: InvitationModalProps) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const modalRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const mutation = usePostAuth<unknown, InvitationPayload>("invitations", {
    onSuccess: () => {
      setMessage({ type: "success", text: "Invitation sent successfully!" });
      setEmail("");

      queryClient.invalidateQueries({
        queryKey: ["get-invitations"],
      });

      setTimeout(() => {
        setMessage({ type: "", text: "" });
      }, 3000);
    },
    onError: (error) => {
      const messageText =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as any).message === "string"
          ? (error as any).message
          : String(error) || "Failed to send invitation.";
      setMessage({
        type: "error",
        text: messageText,
      });
    },
  });

  const { mutateAsync: sendInvitation, isPending } = mutation;

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    if (!isValidEmail(email)) return;

    setMessage({ type: "", text: "" });

    const payload: InvitationPayload = {
      email,
      role: "user",
      accept_url: INVITE_URL,
    };

    try {
      await sendInvitation(payload);
    } catch (error) {
      console.error("Invitation error:", error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValidEmail(email) && !isPending) {
      handleSubmit();
    }
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={handleBackdropClick}
          variants={modalBackdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            ref={modalRef}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
            variants={modalContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-t-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-2">
                <Mail className="h-6 w-6 text-white" />
                <h2 className="text-2xl font-bold text-white">
                  Send Invitation
                </h2>
              </div>
              <p className="text-orange-100 mt-1 text-sm">
                Invite a new user to join Alumnify
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Body */}
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-orange-700 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter email address"
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-orange-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                    autoFocus
                    disabled={isPending}
                  />
                </div>

                <AnimatePresence mode="wait">
                  {message.text && (
                    <motion.div
                      className={`p-4 rounded-lg text-sm font-medium ${
                        message.type === "success"
                          ? "bg-green-50 border-2 border-green-300 text-green-800"
                          : "bg-red-50 border-2 border-red-300 text-red-800"
                      }`}
                      variants={messageVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      key={message.text}
                    >
                      {message.text}
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  onClick={handleSubmit}
                  disabled={!isValidEmail(email) || isPending}
                  className={`w-full py-3 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                    !isValidEmail(email) || isPending
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-orange-600 hover:bg-orange-700 hover:shadow-lg"
                  }`}
                >
                  {isPending ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    "Send Invite"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
